import { FC } from "react";
import { theme } from "src/styles/Theme";
import { Dispatch, SetStateAction, useEffect, useState } from "react";
import { formatDateDmy, formatTime } from "src/utils";

type TimelineEvent = {
  event: string;
  label: string;
  occurred_at: string;
  triggered_by?: string;
};

type BookingTimelineStepperProps = {
  timeline: TimelineEvent[];
  isLoading?: boolean;
  type: string;
  isPartner?: boolean;
  setDTime: Dispatch<SetStateAction<string>>;
};

// Each stage is matched against the incoming timeline by `event` code
// (not by `label`, since backend label copy can change independently of
// the event code). `events` accepts multiple aliases in case the backend
// renames or splits an event over time.

type Stage = {
  label: string;
  events: string[];
};

const DELIVERY_STAGES: Stage[] = [
  { label: "Order Placed", events: ["order_placed"] },
  { label: "Order Accepted", events: ["order_accepted"] },
  { label: "Payment Completed", events: ["payment_completed"] },
  { label: "Rider Assigned", events: ["rider_assigned"] },
  { label: "Order Picked Up", events: ["order_picked_up"] },
  { label: "On the Way", events: ["in_transit"] },
  { label: "Delivered", events: ["delivered"] },
];

const PICKUP_STAGES: Stage[] = [
  { label: "Order Placed", events: ["order_placed"] },
  { label: "Order Accepted", events: ["order_accepted"] },
  { label: "Payment Completed", events: ["payment_completed"] },
  { label: "Order Picked Up", events: ["order_picked_up"] },
];

const BookingTimelineStepper: FC<BookingTimelineStepperProps> = ({
  timeline,
  isLoading,
  type,
  isPartner,
  setDTime,
}) => {
  const stages = type === "delivery_internal" ? DELIVERY_STAGES : PICKUP_STAGES;

  // Map by event code so lookups are stable even if `label` copy changes.
  const eventByCode = new Map<string, TimelineEvent>(
    (timeline || []).map((item) => [item.event, item]),
  );

  const findEventForStage = (stage: Stage): TimelineEvent | undefined => {
    for (const code of stage.events) {
      const match = eventByCode.get(code);
      if (match) return match;
    }
    return undefined;
  };

  let lastCompletedIndex = -1;
  stages.forEach((stage, idx) => {
    if (findEventForStage(stage)) lastCompletedIndex = idx;
  });

  const isCancelled = (timeline || []).some(
    (item) => item.event === "order_cancelled",
  );

  const [orderDel, setOrderDel] = useState("");

  useEffect(() => {
    const pickedUp = timeline?.find((item) => item.event === "order_picked_up");

    if (pickedUp) {
      setOrderDel(pickedUp.occurred_at);
      setDTime(pickedUp.occurred_at)
    }
  }, [timeline]);
  useEffect(() => {
    const pickedUp = timeline?.find((item) => item.event === "delivered");

    if (pickedUp) {
      setOrderDel(pickedUp.occurred_at);
      setDTime(pickedUp.occurred_at)
    }
  }, [timeline]);

  console.log("order_del");
  console.log(orderDel);
  // setPDTime

  if (isLoading) {
    return (
      <div className="bg-white rounded-xl shadow p-6 mb-4">
        <div className="h-4 w-32 bg-gray-100 rounded animate-pulse mb-6" />
        <div className="flex items-center">
          {[0, 1, 2, 3].map((i) => (
            <div key={i} className="flex-1 flex flex-col items-center relative">
              <div className="w-9 h-9 rounded-full bg-gray-100 animate-pulse z-10 relative" />
              {i < 3 && (
                <div className="absolute top-[18px] left-1/2 w-full h-[2px] bg-gray-100" />
              )}
            </div>
          ))}
        </div>
      </div>
    );
  }

  if (!isPartner) {
    return null;
  }

  return (
    <div className="bg-white rounded-xl shadow p-6 mb-4 overflow-x-auto">
      <div className="flex items-center justify-between mb-8">
        <div className="font-semibold text-lg text-gray-900">
          {type === "delivery_internal" ? "Delivery" : "Pick-up"} Timeline
        </div>
        {isCancelled && (
          <span
            className="text-xs font-semibold px-3 py-1 rounded-full text-white"
            style={{ backgroundColor: theme.colors.red }}
          >
            Cancelled
          </span>
        )}
      </div>

      <div
        className="flex items-start"
        style={{ minWidth: stages.length * 90 }}
      >
        {stages.map((stage, idx) => {
          const eventData = findEventForStage(stage);
          const isCompleted = idx <= lastCompletedIndex && !isCancelled;
          const isCurrent = idx === lastCompletedIndex && !isCancelled;
          const isPending = !isCompleted;
          const isLast = idx === stages.length - 1;
          const connectorFilled = idx < lastCompletedIndex && !isCancelled;

          const nodeColor = isCancelled
            ? "#d1d5db"
            : isCompleted
              ? theme.colors.purple
              : "#d1d5db";

          return (
            <div
              key={stage.label}
              className="flex-1 flex flex-col items-center relative"
            >
              {/* Connector line — drawn from center of this node to center of next */}
              {!isLast && (
                <div
                  className="absolute h-[2px] transition-all"
                  style={{
                    top: 18,
                    left: "50%",
                    width: "100%",
                    backgroundColor: connectorFilled
                      ? theme.colors.purple
                      : "#e5e7eb",
                    zIndex: 0,
                  }}
                />
              )}

              {/* Node */}
              <div
                className="w-9 h-9 rounded-full flex items-center justify-center font-semibold text-sm transition-all relative z-10"
                style={{
                  backgroundColor: nodeColor,
                  color: isCompleted && !isCancelled ? "#fff" : "#9ca3af",
                  boxShadow: isCurrent
                    ? `0 0 0 4px ${theme.colors.purple}26`
                    : "none",
                  border: isPending ? "2px solid #e5e7eb" : "none",
                }}
              >
                {idx + 1}
              </div>

              {/* Label — centered under node */}
              <div className="mt-3 flex flex-col items-center text-center w-full">
                <div
                  className={`text-[11px] leading-tight ${
                    isCompleted && !isCancelled
                      ? isCurrent
                        ? "font-semibold text-gray-900"
                        : "font-medium text-gray-700"
                      : "font-medium text-gray-400"
                  }`}
                >
                  {stage.label}
                </div>
                {eventData ? (
                  <>
                    <div className="text-[10px] text-gray-400 mt-1 leading-tight">
                      {formatDateDmy(eventData.occurred_at)}
                    </div>
                    <div className="text-[10px] text-gray-400 leading-tight">
                      {formatTime(new Date(eventData.occurred_at))}
                    </div>
                  </>
                ) : (
                  <div className="text-[10px] text-gray-300 mt-1">Pending</div>
                )}
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
};

export default BookingTimelineStepper;
